import React, { useEffect, useMemo, useState } from "react"
import { Button } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { parseTemplate } from "api/Engine/templates"
import { Loader } from "shared/ui/Loader/Loader.component"
import { failureOf } from "utils/errMsgOf"
import { QuestionnaireNames } from "utils/questionnaireNames"
import {
  StagedBlock,
  TemplateCatalogueItem,
  TemplateParseResult,
} from "types/template.types"
import TemplateInputField from "./TemplateInputField.component"
import {
  InputStates,
  allInputs,
  exceptionChoices,
  highlightParts,
  highlightTerms,
  initialStates,
  inputValues,
  isMissing,
  tickedExceptions,
} from "./templateInputs"
import { splitKeyword } from "./templateText"
import { useTemplatePreview } from "./useTemplatePreview"

interface TemplateWizardProps {
  item: TemplateCatalogueItem
  projectId: string
  /** The project owner (hint_owner of the template routes) */
  owner: string
  /** The names the questionnaire already uses */
  names: QuestionnaireNames
  /** The NAME values of the blocks already in the tray */
  stagedNames: string[]
  /** The staged block being edited again */
  initial?: StagedBlock
  onAdd: (block: StagedBlock) => void
  onBack: () => void
}

/** A section's SHOW text without its markup, as a group heading. */
const plainShow = (show: string): string =>
  show
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()

/** The merged text, keywords in green and the user's values marked. */
const PreviewText: React.FC<{ text: string; terms: string[] }> = ({
  text,
  terms,
}) => (
  <>
    {text.split(/\r?\n/).map((line, i) => {
      const { indent, keyword, rest } = splitKeyword(line)
      return (
        <React.Fragment key={i}>
          {i > 0 && "\n"}
          {indent}
          {keyword && <span className="tpl-kw">{keyword}</span>}
          {highlightParts(rest, terms).map((part, j) =>
            part.mark ? (
              <mark key={j} className="tpl-fill">
                {part.text}
              </mark>
            ) : (
              <React.Fragment key={j}>{part.text}</React.Fragment>
            )
          )}
        </React.Fragment>
      )
    })}
  </>
)

/** The form and preview, once the template is parsed. */
const WizardBody: React.FC<
  Omit<TemplateWizardProps, "onBack"> & {
    parse: TemplateParseResult
    onCancel: () => void
  }
> = ({
  item,
  projectId,
  owner,
  names,
  stagedNames,
  initial,
  onAdd,
  onCancel,
  parse,
}) => {
  const [states, setStates] = useState<InputStates>(() =>
    initialStates(parse, names, initial)
  )

  const values = useMemo(() => inputValues(parse, states), [parse, states])
  const exceptions = useMemo(
    () => tickedExceptions(parse, states),
    [parse, states]
  )
  const preview = useTemplatePreview(
    {
      id: projectId,
      owner,
      file: item.file,
      access: item.access,
      template: item.template,
    },
    values,
    exceptions
  )
  const terms = useMemo(
    () =>
      highlightTerms([
        ...values,
        { label: "", value: exceptions.join("\n") },
      ]),
    [values, exceptions]
  )

  const blocked = allInputs(parse).some((input) =>
    isMissing(input, states[input.label])
  )

  const add = () => {
    if (blocked) return
    onAdd({
      id: initial?.id ?? crypto.randomUUID(),
      ref: { file: item.file, access: item.access, template: item.template },
      title: item.label,
      inputs: values,
      exceptions,
    })
  }

  return (
    <>
      <div className="tpl-wizard-head">
        <Button
          type="text"
          icon={<FontAwesomeIcon icon={faArrowLeft} />}
          onClick={onCancel}
        >
          Templates
        </Button>
        <span className="tpl-wizard-title">{item.label}</span>
        <span className="tpl-wizard-desc">{item.description}</span>
        <span className="tpl-wizard-actions">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" disabled={blocked} onClick={add}>
            Add
          </Button>
        </span>
      </div>
      <div className="tpl-wizard-main">
        <div className="tpl-wizard-inputs">
          {parse.sections.map((section, s) => {
            const heading = plainShow(section.show)
            return (
              <div className="tpl-group" key={s}>
                {heading && <div className="tpl-group-title">{heading}</div>}
                {section.inputs.map((input) => {
                  const state = states[input.label]
                  return (
                    <TemplateInputField
                      key={input.label}
                      input={input}
                      state={state}
                      onChange={(next) =>
                        setStates((prev) => ({ ...prev, [input.label]: next }))
                      }
                      names={names}
                      stagedNames={stagedNames}
                      merged={preview.text}
                      exceptionChoices={exceptionChoices(
                        input,
                        parse,
                        names,
                        state.ticked
                      )}
                      missing={isMissing(input, state)}
                    />
                  )
                })}
              </div>
            )
          })}
          {allInputs(parse).length === 0 && (
            <div className="tpl-field-hint">
              This template asks for nothing; Add puts it in as it is.
            </div>
          )}
        </div>
        <div className="tpl-wizard-preview">
          <div className="tpl-wizard-preview-head">
            Will be inserted at the cursor
            {preview.loading && <span className="tpl-wizard-busy">Updating</span>}
          </div>
          <pre className="tpl-code" data-testid="template-preview">
            {preview.error ? (
              <span className="tpl-preview-error">{preview.error}</span>
            ) : (
              <PreviewText text={preview.text} terms={terms} />
            )}
          </pre>
        </div>
      </div>
    </>
  )
}

/**
 * The one-page template wizard: the template's inputs on the left, the
 * server's merge of them on the right (with the user's values marked), and
 * "Add", which hands a staged block to the tray. Required inputs left empty
 * block Add; a NAME already in use gets a warning (the tray's server check
 * is the one that counts).
 *
 * @param props - The component props
 * @returns The wizard
 */
const TemplateWizard: React.FC<TemplateWizardProps> = ({ onBack, ...props }) => {
  const getAxios = useAuthedAxios()
  const { projectId, owner, item } = props
  const [parse, setParse] = useState<TemplateParseResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setParse(null)
    setError(null)
    void (async () => {
      try {
        const answer = await parseTemplate(await getAxios(), {
          id: projectId,
          owner,
          file: item.file,
          access: item.access,
          template: item.template,
        })
        if (live) setParse(answer.result)
      } catch (e) {
        if (live) setError(failureOf(e))
      }
    })()
    return () => {
      live = false
    }
  }, [getAxios, projectId, owner, item.file, item.access, item.template])

  return (
    <div className="tpl-wizard" data-testid="template-wizard">
      {parse ? (
        <WizardBody {...props} parse={parse} onCancel={onBack} />
      ) : (
        <>
          <div className="tpl-wizard-head">
            <Button
              type="text"
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={onBack}
            >
              Templates
            </Button>
            <span className="tpl-wizard-title">{item.label}</span>
          </div>
          <div className="tpl-state">
            {error ? (
              `The template could not be loaded: ${error}`
            ) : (
              <Loader fontSize={28} />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default TemplateWizard
